<template>
  <file-card-container :error="uploadError" :success="hasUploadedSuccessfully">
    <i
      v-if="uploadError"
      class="file-card-indicator icon-notification2 text-danger"
    />

    <i
      v-if="hasUploadedSuccessfully"
      class="file-card-indicator icon-checkmark-circle text-success"
    />

    <div class="file-card-icon-holder mr-2 align-self-center">
      <i v-if="uploading" class="file-card-icon file-card-progress-icon icon-spinner2 spinner"/>
      <i v-else :class="[fileIcon, fileColor]" class="file-card-icon icon-2x top-0"/>

      <button
        class="btn btn-link file-card-delete-button"
        type="button"
        @click="remove"
      >
        <delete-icon/>
      </button>
    </div>

    <div class="file-card-body">
      <span v-if="isTemporaryFile && !filePreview" :title="fileName" class="file-card-title">{{ fileName }}</span>
      <a
        v-else
        :href="fileUrl"
        :title="fileName"
        class="file-card-title"
        target="_blank"
      >
        {{ fileName }}
      </a>

      <ul class="list-inline font-size-sm mb-0">
        <li class="file-card-size">{{ $t('fields.size') }}: {{ fileSize }} MB</li>
      </ul>
    </div>

    <div
      v-if="withMain && !uploading"
      class="file-card-icon-holder align-self-center"
    >
      <s-checkbox
        v-tippy
        :content="$t('fields.main_file')"
        :model-value="isFileMain"
        @click="main"
      />
    </div>

    <div
      v-if="uploading"
      :style="{ width: file.vue.progress + '%' }"
      class="progress-bar progress-bar-striped progress-bar-animated bg-success"
    />
  </file-card-container>
</template>

<script>
  import FileCardContainer from '@/components/files/file-card-container';
  import { getFileColorForIcon, getFileIcon } from '@/components/files/icons';
  import DeleteIcon from '@/components/icons/delete';

  export default {
    name: 'FileCard',

    components: { FileCardContainer, DeleteIcon },

    props: {
      file: {
        // [Object, File], Object is not File, Vue with File might throw if File is not available
        type: null,
        required: true,
      },
      withMain: {
        type: Boolean,
        default: false,
      },
      mainFileId: {
        type: [String, Boolean],
        default: null,
      },
      filePreview: {
        type: Boolean,
        default: false,
      },
    },

    computed: {
      isTemporaryFile() {
        return Boolean(this.file.vue);
      },

      uploading() {
        if (!this.file.vue) {
          return false;
        }

        return this.file.vue.busy;
      },

      uploadError() {
        if (!this.file.vue || !this.file.vue.error) {
          return null;
        }

        return this.file.vue.error.message;
      },

      hasUploadedSuccessfully() {
        return this.isTemporaryFile && !this.uploading && !this.uploadError;
      },

      fileName() {
        return this.file.name;
      },

      fileSize() {
        if ('File' in window && this.file instanceof File) {
          return (this.file.size / (1024 * 1024)).toFixed(2);
        }

        if (this.isTemporaryFile) {
          return (this.file.file.size / (1024 * 1024)).toFixed(2);
        }

        return this.file.size;
      },

      fileUrl() {
        if ('File' in window && this.file instanceof File) {
          return null;
        }

        if (this.isTemporaryFile) {
          return `${process.env.AWS_CLOUDFRONT_URL}/${this.file?.vue?.entry?.key}`;
        }

        return `${process.env.AWS_CLOUDFRONT_URL}/${this.file.path}`;
      },

      fileIcon() {
        return getFileIcon(this.fileName);
      },

      fileColor() {
        return getFileColorForIcon(this.fileIcon);
      },

      isFileMain() {
        if (!this.mainFileId || !this.withMain) {
          return false;
        }

        if (this.isTemporaryFile) {
          return this.file.vue.entry.uid === this.mainFileId;
        }

        return this.file.id === this.mainFileId;
      },
    },

    methods: {
      remove() {
        this.$emit('remove', this.file);
      },

      main() {
        this.$emit('main', this.file);
      },
    },
  };
</script>
